/** Bounded product measurements. Never accept records, form values, URLs or raw errors. */
export const PRODUCT_ACTIONS = ['login', 'login_otp_request', 'login_otp_verify', 'attendance_check_in', 'attendance_check_out_prepare', 'attendance_check_out', 'attendance_random', 'task_submit', 'task_create', 'task_update', 'lms_start', 'lms_complete', 'training_save', 'lms_content_save', 'lms_journey_save', 'lms_session_save', 'lms_content_publish', 'approval_decide'] as const;
export type ProductAction = typeof PRODUCT_ACTIONS[number];
export const PRODUCT_REASONS = ['validation', 'permission_denied', 'network', 'timeout', 'server', 'business_rule', 'cancelled', 'unknown'] as const;
export type ProductFailureReason = typeof PRODUCT_REASONS[number];
const PROPERTY_ENUMS = {
  entry_point: ['login_form', 'otp_initial', 'otp_resend', 'otp_verify', 'task_editor', 'task_list', 'approval_queue', 'lms_editor', 'lms_player', 'attendance', 'attendance_start', 'attendance_end', 'attendance_random', 'home', 'notification'],
  attendance_mode: ['daily', 'random', 'store', 'remote', 'selfie', 'confirm_only'],
  task_type: ['assigned', 'self', 'scheduled', 'ad_hoc', 'unknown'],
  content_type: ['slides', 'scorm', 'session', 'journey', 'video', 'document', 'quiz', 'unknown'],
  approval_type: ['leave', 'regularization', 'compoff', 'store', 'attendance', 'task', 'unknown'],
  decision: ['approve', 'reject'],
  save_mode: ['create', 'update'],
  stage: ['camera', 'location', 'submission'],
} as const;
export type ProductActionProperties = { [K in keyof typeof PROPERTY_ENUMS]?: typeof PROPERTY_ENUMS[K][number] };
export const PRODUCT_REQUESTS = ['login', 'attendance', 'task', 'lms', 'approval', 'dashboard', 'notification', 'task_list', 'approval_queue', 'lms_content_list', 'lms_session_list'] as const;
export type ProductRequest = typeof PRODUCT_REQUESTS[number];
export type ProductRequestOutcome = 'success' | 'failure' | 'cancelled';
export type ProductRequestProperties = { cache_status?: 'hit' | 'miss' | 'unknown'; status_class?: '2xx' | '3xx' | '4xx' | '5xx' | 'network' | 'unknown' };
export const NOTIFICATION_TYPES = ['attendance', 'random_attendance', 'training', 'onboarding', 'task', 'approval', 'social', 'celebration', 'other'] as const;
export type NotificationType = typeof NOTIFICATION_TYPES[number];
export interface NotificationOpenProperties {
  notification_type: NotificationType;
  channel: 'push' | 'local';
  notification_trace_id: string;
  attribution_action?: ProductAction;
}
export const PRODUCT_EVENTS = ['product_action_started', 'product_action_completed', 'product_action_failed', 'product_action_cancelled', 'product_request_completed', 'notification_opened'] as const;
export type ProductEvent = typeof PRODUCT_EVENTS[number];
export interface ProductActionAttempt {
  succeed(properties?: ProductActionProperties): boolean;
  fail(reason: ProductFailureReason, properties?: ProductActionProperties): boolean;
  cancel(reason?: ProductFailureReason): boolean;
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ATTEMPT_ID = /^[0-9a-f]{32}$/;
const MAX_ELAPSED_MS = 24 * 60 * 60 * 1000;
const ATTRIBUTION_TTL_MS = 30 * 60 * 1000;
const validEnum = (values: readonly string[], value: unknown): value is string => typeof value === 'string' && values.includes(value);
const elapsed = (value: unknown): number | undefined => typeof value === 'number' && Number.isFinite(value) ? Math.min(MAX_ELAPSED_MS, Math.max(0, Math.round(value))) : undefined;

export function sanitizeProductActionProperties(input: unknown): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (!input || typeof input !== 'object') return result;
  for (const [key, values] of Object.entries(PROPERTY_ENUMS)) {
    const value = (input as Record<string, unknown>)[key];
    if (validEnum(values, value)) result[key] = value;
  }
  return result;
}

/** Inspect only machine status/code fields; never classify by raw error text. */
export function classifyProductFailure(error: unknown): ProductFailureReason {
  if (!error || typeof error !== 'object') return 'unknown';
  const value = error as { code?: unknown; status?: unknown; response?: { status?: unknown } };
  if (value.code === 'ECONNABORTED' || value.code === 'ETIMEDOUT') return 'timeout';
  if (value.code === 'ERR_CANCELED' || value.code === 'ABORT_ERR') return 'cancelled';
  if (value.code === 'ERR_NETWORK' || value.code === 'ENETUNREACH') return 'network';
  const status = value.response?.status ?? value.status;
  if (status === 401 || status === 403) return 'permission_denied';
  if (status === 400 || status === 422) return 'validation';
  if (typeof status === 'number' && status >= 500 && status <= 599) return 'server';
  if (typeof status === 'number' && status >= 400 && status <= 499) return 'business_rule';
  return 'unknown';
}

/** Strict gate for new events, including SDK enrichment. No arbitrary property names survive. */
export function sanitizeProductEventProperties(event: string, input: Record<string, unknown>): Record<string, unknown> | null {
  if (!validEnum(PRODUCT_EVENTS, event)) return null;
  const result: Record<string, unknown> = {};
  if (event.startsWith('product_action_')) {
    if (!validEnum(PRODUCT_ACTIONS, input.action) || typeof input.attempt_id !== 'string' || !ATTEMPT_ID.test(input.attempt_id)) return null;
    Object.assign(result, sanitizeProductActionProperties(input), { action: input.action, attempt_id: input.attempt_id });
    if (event === 'product_action_failed' || event === 'product_action_cancelled') {
      result.reason_code = validEnum(PRODUCT_REASONS, input.reason_code) ? input.reason_code : 'unknown';
    }
  } else if (event === 'product_request_completed') {
    if (!validEnum(PRODUCT_REQUESTS, input.operation) || !validEnum(['success', 'failure', 'cancelled'], input.outcome)) return null;
    result.operation = input.operation;
    result.outcome = input.outcome;
    if (validEnum(['hit', 'miss', 'unknown'], input.cache_status)) result.cache_status = input.cache_status;
    if (validEnum(['2xx', '3xx', '4xx', '5xx', 'network', 'unknown'], input.status_class)) result.status_class = input.status_class;
  } else {
    if (!validEnum(NOTIFICATION_TYPES, input.notification_type) || !validEnum(['push', 'local'], input.channel) || typeof input.notification_trace_id !== 'string' || !UUID.test(input.notification_trace_id)) return null;
    result.channel = input.channel;
  }
  const duration = elapsed(input.elapsed_ms);
  if (event !== 'notification_opened') {
    if (duration === undefined) return null;
    result.elapsed_ms = duration;
  }
  if (validEnum(NOTIFICATION_TYPES, input.notification_type) && typeof input.notification_trace_id === 'string' && UUID.test(input.notification_trace_id)) {
    result.notification_type = input.notification_type;
    result.notification_trace_id = input.notification_trace_id.toLowerCase();
  }
  // Existing opaque identity and bounded technical context are needed by PostHog.
  for (const key of ['distinct_id', '$device_id', '$session_id', '$window_id']) {
    const value = input[key];
    if (typeof value === 'string' && /^[a-z0-9_-]{1,128}$/i.test(value)) result[key] = value;
  }
  const contextEnums: Record<string, readonly string[]> = {
    app: ['channelplex'], surface: ['web', 'mobile'], platform: ['ios', 'android'],
    env: ['production', 'development', 'preview', 'local', 'test', 'staging', 'qa', 'uat', 'preproduction', 'unknown'],
    role_category: ['L1', 'L2', 'L3', 'L4', 'L5'],
    admin_role: ['none', 'admin', 'super_admin'], user_type: ['employee', 'external'],
    $lib: ['posthog-js', 'posthog-react-native', 'web'],
  };
  for (const [key, values] of Object.entries(contextEnums)) {
    if (validEnum(values, input[key])) result[key] = input[key];
  }
  for (const key of ['$lib_version', '$app_version', '$app_build']) {
    const value = input[key];
    if (typeof value === 'string' && /^[0-9][a-z0-9_.-]{0,31}$/i.test(value)) result[key] = value;
  }
  if (typeof input.project_id === 'number' && Number.isSafeInteger(input.project_id) && input.project_id > 0) result.project_id = input.project_id;
  if (typeof input.$is_emulator === 'boolean') result.$is_emulator = input.$is_emulator;
  for (const key of ['$process_person_profile', '$is_identified']) {
    if (typeof input[key] === 'boolean') result[key] = input[key];
  }
  return result;
}

/** Retain SDK event deduplication/time fields without arbitrary top-level data. */
export function productEventMetadata(input: { uuid?: unknown; timestamp?: unknown }): { uuid?: string; timestamp?: Date } {
  return {
    ...(typeof input.uuid === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(input.uuid) ? { uuid: input.uuid } : {}),
    ...(input.timestamp instanceof Date && Number.isFinite(input.timestamp.getTime()) ? { timestamp: input.timestamp } : {}),
  };
}

function randomAttemptId(): string {
  const bytes = new Uint8Array(16);
  try {
    if (typeof globalThis.crypto?.getRandomValues !== 'function') throw new Error('unavailable');
    globalThis.crypto.getRandomValues(bytes);
  } catch {
    // This is a correlation ID, never an authentication or authorization token.
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}
const ATTRIBUTABLE_ACTIONS: Partial<Record<NotificationType, readonly ProductAction[]>> = {
  attendance: ['attendance_check_in', 'attendance_check_out'],
  random_attendance: ['attendance_random'],
  training: ['lms_start', 'lms_complete'],
  task: ['task_submit'],
  approval: ['approval_decide'],
};

export function createProductMeasurementController(options: {
  context: () => string | null;
  capture: (event: ProductEvent, properties: Record<string, unknown>) => boolean;
  now?: () => number;
  attemptId?: () => string;
  /**
   * Whether this action's start is sent as its own event. Defaults to always. Without it an
   * attempt is only seen through its terminal, which carries the same attempt_id and
   * elapsed_ms; the mobile client sends no starts, the web client sends them all.
   */
  emitStartEvent?: (action: ProductAction) => boolean;
}) {
  const now = options.now ?? (() => Date.now());
  let attribution: (NotificationOpenProperties & { at: number; context: string }) | null = null;
  const emit = (event: ProductEvent, input: Record<string, unknown>): boolean => {
    try {
      const properties = sanitizeProductEventProperties(event, input);
      return properties !== null && options.capture(event, properties);
    } catch { return false; }
  };
  const getAttribution = (action: ProductAction, context: string): Record<string, unknown> => {
    if (!attribution) return {};
    const age = now() - attribution.at;
    if (context !== attribution.context || age < 0 || age > ATTRIBUTION_TTL_MS) { attribution = null; return {}; }
    if (!ATTRIBUTABLE_ACTIONS[attribution.notification_type]?.includes(action) || (attribution.attribution_action && attribution.attribution_action !== action)) return {};
    return { notification_trace_id: attribution.notification_trace_id, notification_type: attribution.notification_type };
  };
  return {
    beginProductAction(action: ProductAction, properties: ProductActionProperties = {}): ProductActionAttempt {
      const context = options.context();
      const start = now();
      const attemptId = (options.attemptId ?? randomAttemptId)();
      const initial = { ...sanitizeProductActionProperties(properties), ...(context === null ? {} : getAttribution(action, context)), action, attempt_id: attemptId };
      const emitsStart = options.emitStartEvent?.(action) ?? true;
      // A skipped start still opens the attempt; only a failed start capture may close it,
      // so the terminal of a start-less attempt is still sent.
      const started = context !== null && (!emitsStart || emit('product_action_started', { ...initial, elapsed_ms: 0 }));
      let terminal = false;
      const finish = (event: ProductEvent, reason?: ProductFailureReason, terminalProperties?: ProductActionProperties): boolean => {
        if (terminal) return false;
        terminal = true;
        if (!started || context !== options.context()) return false;
        return emit(event, { ...initial, ...sanitizeProductActionProperties(terminalProperties), elapsed_ms: now() - start, ...(reason ? { reason_code: reason } : {}) });
      };
      return {
        succeed: (extra) => finish('product_action_completed', undefined, extra),
        fail: (reason, extra) => finish('product_action_failed', reason, extra),
        cancel: (reason = 'cancelled') => finish('product_action_cancelled', reason),
      };
    },
    beginProductRequest(operation: ProductRequest) {
      const context = options.context();
      const start = now();
      let terminal = false;
      const complete = (outcome: ProductRequestOutcome, properties: ProductRequestProperties = {}): boolean => {
        if (terminal) return false;
        terminal = true;
        if (context === null || context !== options.context()) return false;
        return emit('product_request_completed', { ...properties, operation, outcome, elapsed_ms: now() - start });
      };
      return {
        complete,
        succeed: (properties?: ProductRequestProperties) => complete('success', properties),
        fail: (properties?: ProductRequestProperties) => complete('failure', properties),
        cancel: () => complete('cancelled'),
      };
    },
    captureNotificationOpened(properties: NotificationOpenProperties): boolean {
      const context = options.context();
      if (context === null || !emit('notification_opened', { ...properties })) return false;
      attribution = {
        notification_type: properties.notification_type,
        channel: properties.channel,
        notification_trace_id: properties.notification_trace_id.toLowerCase(),
        ...(validEnum(PRODUCT_ACTIONS, properties.attribution_action) ? { attribution_action: properties.attribution_action } : {}),
        at: now(), context,
      };
      return true;
    },
    clearAttribution() { attribution = null; },
  };
}
